import React, { useEffect, useState } from 'react';
import { Box, CustomModalLayout } from '@wix/design-system';
import { AppPageLink } from '../types';
import type { AppPageOptions } from '../types';
import { AppPageComponent } from '../app-page';
import { getAppModalData } from './app-modal-data';
import type { getReviewsSectionResponse } from '../app-page-sections/reviews/reviews-api';
import { CenteredLoader } from '../../../components/centered-loader';
import { AppPageLeanHeader } from '@wix/app-market-components';
import {
  useEssentials,
  useServices,
  useSiteContext,
  useComponentLoadReporter,
} from '@wix/app-market-core';
import s from './app-modal.scss';
import { useQuery } from '@tanstack/react-query';
import { appModalId } from './app-modal-id';
import { shouldShowPermissionsAndTerms } from '../utils/permissionsAndTerms';
import AppModalContentPermissions from './app-modal-components/app-modal-content-premissions';
import AppModalContentSideActions from './app-modal-components/app-modal-content-side-actions';
import type {
  AppModalDisplayOptions,
  OnError,
} from '@wix/app-market-core-components-types';
import { AppPageType } from '@wix/app-market-core-components-types';
import { appMarketAppPageClick } from '@wix/bi-logger-app-market-data/v2';

export interface AppModalContentProps {
  id: string;
  onClose: () => void;
  onLoad?: () => void;
  options?: AppModalDisplayOptions;
  onError?: OnError;
  origin: string;
}

export function AppModalContent({
  id,
  onClose,
  onLoad,
  options = {},
  onError,
  origin,
}: AppModalContentProps) {
  const siteContext = useSiteContext();
  const { languageCode, isPremium } = siteContext;
  const queryKey = [appModalId, languageCode, id];
  const reportLoadFinish = useComponentLoadReporter({
    componentId: appModalId,
    params: { appId: id },
    deps: queryKey,
  });
  const services = useServices();
  const { experiments, biLogger, errorHandler } = useEssentials();

  const [reviews, setReviews] = useState<
    getReviewsSectionResponse | undefined
  >();
  const defaultOnError = (error: unknown) => {
    errorHandler?.showError(error);
  };

  const appPageType = AppPageType.ONE_STEP_SLIM;

  const isMinimalSkin = options?.skin === 'lean' || options?.skin === 'slim';
  const shouldShowLeanHeader = options?.skin === 'lean';
  const isNewSlimSkin = options?.skin === 'slim';

  const fetchOptions =
    options?.skin === 'lean'
      ? {
          fetchPermissions: true,
          fetchManagedApps: false,
        }
      : options?.skin === 'slim'
      ? {
          fetchPermissions: true,
          fetchManagedApps: true,
        }
      : undefined;

  const appOptions: AppPageOptions = {
    showCtas: true,
    showRecommendedForYou: false,
    showHeaderLinks: false,
    showFeaturedIn: false,
    disabledLinks: [AppPageLink.COMPANY_PAGE],
    stickyHeaderOptions: {
      showCTA: false,
      showAppInfoBadge: false,
      showBackButton: false,
      showRatingInfoBadge: isNewSlimSkin ? true : false,
      showCompany: isNewSlimSkin ? false : true,
    },
    pricingOptions: {
      showExternalPricingLink: false,
      showNarrowPlansView: true,
    },
    showModalLayout: true,
    showTermsAndConditions: false,
    showAppPageLink: true,
    ...options,
  };

  const { data: appModalData } = useQuery({
    queryKey,
    queryFn: async () => {
      return getAppModalData({
        services,
        languageCode,
        siteContext,
        experiments,
        id,
        options: fetchOptions,
      });
    },
    onError: (error) => {
      onError ? onError(error as Error) : defaultOnError(error);
    },
  });

  useEffect(() => {
    if (appModalData) {
      onLoad?.();
    }
  }, [appModalData]);

  const shouldShowPermissions = shouldShowPermissionsAndTerms(
    appModalData?.permissions ?? [],
    appModalData?.app.installation?.isInstalled ?? false,
  );

  const ModalContent = (
    <div
      className={`${s.modalContentWrapper} ${
        isMinimalSkin ? s.hiddenOverflow : ''
      }`}
    >
      {!appModalData ? (
        <Box
          align="center"
          justifyItems="center"
          className={isMinimalSkin ? s.minimalSkin : s.regularSkin}
        >
          <CenteredLoader />
        </Box>
      ) : (
        <Box
          direction="vertical"
          className={isNewSlimSkin ? s.newSlimSkin : ''}
        >
          {shouldShowLeanHeader && (
            <AppPageLeanHeader
              isPremiumSite={isPremium}
              experiments={experiments}
              appData={appModalData.app}
              languageCode={languageCode}
              image={appModalData.app.icon}
              showDivider={options?.skin === 'lean'}
            />
          )}
          <AppPageComponent
            {...appModalData}
            options={appOptions}
            reviews={reviews ?? appModalData.reviews}
            onChange={(newReviews: getReviewsSectionResponse) => {
              setReviews(newReviews);
            }}
            onLoad={reportLoadFinish}
            appPageType={appPageType}
          />
        </Box>
      )}
    </div>
  );

  if (!appOptions.showModalLayout) {
    return ModalContent;
  }

  return (
    <CustomModalLayout
      dataHook="app-modal-content"
      className={s.appModal}
      width="930px"
      maxHeight={isMinimalSkin ? '600px' : '960px'}
      overflowY="hidden"
      onCloseButtonClick={() => {
        biLogger.report(
          appMarketAppPageClick({
            app_id: appModalData?.app.id,
            cta: 'cancel',
            location: 'slim_app_page',
            app_page_type: appPageType,
          }),
        );
        onClose();
      }}
      removeContentPadding
      showFooterDivider
      content={ModalContent}
      footnote={
        appOptions.showTermsAndConditions &&
        appModalData != null &&
        shouldShowPermissions && (
          <AppModalContentPermissions
            appModalData={appModalData}
            languageCode={languageCode}
            appPageType={appPageType}
          />
        )
      }
      sideActions={
        appModalData == null ? (
          // TODO change to Skeleton
          <Box minHeight="30px" />
        ) : (
          <AppModalContentSideActions
            showTermsAndConditions={appOptions.showTermsAndConditions ?? false}
            appModalData={appModalData}
            shouldShowPermissions={shouldShowPermissions}
            showAppPageLink={!!appOptions?.showAppPageLink}
            onClose={onClose}
            origin={origin}
          />
        )
      }
    />
  );
}
